import React, { useEffect, useState } from "react";
import { z } from "zod";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { useNavigate } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { resetLoading, resetPassword } from "redux/features/auth/authSlice";
import { motion, AnimatePresence } from "framer-motion";
import CommonLayout from "./CommonLayout";
import { Eye, EyeOff, Check, X, Shield, Lock, AlertCircle, KeyRound } from "lucide-react";

const formSchema = z
  .object({
    email: z.string().email({ message: "Veuillez entrer une adresse email valide." }),
    oldPassword: z.string().min(1, { message: "Le mot de passe actuel est obligatoire." }),
    newPassword: z
      .string()
      .regex(
        /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?À-ÿ]).{8,26}$/,
        "Le mot de passe doit contenir au moins 1 lettre minuscule, 1 lettre majuscule, 1 chiffre et 1 caractère spécial."
      ),
    passwordConfirm: z.string(),
  })
  .refine((data) => data.newPassword === data.passwordConfirm, {
    message: "Le mode de passe ne correspondent pas.",
    path: ["passwordConfirm"],
  });

const ClientAccountPass = () => {
  const dispatch = useAppDispatch();
  const userState = useAppSelector((state) => state?.auth?.user);
  const navigate = useNavigate();
  const [errorForm, setErrorForm] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: userState?.email },
    mode: "onSubmit",
  });

  useEffect(() => {
    const updatedRequirements = {
      minLength: getValues("newPassword")?.length >= 8,
      hasLowercase: /[a-z]/.test(getValues("newPassword")),
      hasUppercase: /[A-Z]/.test(getValues("newPassword")),
      hasNumber: /[0-9]/.test(getValues("newPassword")),
      hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?À-ÿ]/.test(getValues("newPassword")),
    };

    setPasswordRequirements(updatedRequirements);
  }, [watch("newPassword")]);

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = async (data) => {
    if (data.newPassword !== data.passwordConfirm) {
      setValue("passwordConfirm", "", {
        shouldValidate: true,
      });
      return;
    }
    const result = await dispatch(
      resetPassword({
        ...data,
      })
    );

    if (result.payload && result.payload.status === "VALIDATION" && result.payload.error?.length > 0) {
      setErrorForm(true);
    } else {
      navigate("/client");
      dispatch(resetLoading());
    }
  };

  useEffect(() => {
    dispatch(resetLoading());
  }, []);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const authState = useAppSelector((state) => state.auth);
  useEffect(() => {
    switch (authState.loading) {
      case "pending": {
        setIsLoading(true);
        break;
      }
      case "succeeded": {
        setIsLoading(false);
        break;
      }
      case "failed": {
        setIsLoading(false);
        break;
      }
    }
  }, [authState.loading]);

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const requirementVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
  };

  return (
    <CommonLayout>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-6"
      >
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <motion.div variants={itemVariants} className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl">
                  <Shield className="w-10 h-10 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                  <KeyRound className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
              Sécurité du compte
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Mettez à jour votre mot de passe pour protéger votre compte et vos données personnelles
            </p>
          </motion.div>

          {/* Error Alert */}
          <AnimatePresence>
            {errorForm && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="mb-8"
              >
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-700 dark:text-red-300 text-sm font-medium">
                    Vérifiez votre ancien mot de passe
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main Form Card */}
          <motion.div
            variants={itemVariants}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <div className="p-8">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Left Column - Password Fields */}
                  <div className="space-y-6">
                    {/* Current Password */}
                    <motion.div variants={itemVariants} className="space-y-2">
                      <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        <Lock className="w-4 h-4" />
                        <span>Mot de passe actuel</span>
                      </label>
                      <div className="relative group">
                        <input
                          type={showPassword ? "text" : "password"}
                          placeholder="Entrez votre mot de passe actuel"
                          className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-12"
                          {...register("oldPassword")}
                        />
                        <motion.button
                          type="button"
                          whileTap={{ scale: 0.95 }}
                          onClick={togglePasswordVisibility}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </motion.button>
                      </div>
                      <AnimatePresence>
                        {errors.oldPassword?.message && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="text-red-500 text-sm flex items-center space-x-1"
                          >
                            <X className="w-4 h-4" />
                            <span>{errors.oldPassword.message}</span>
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </motion.div>

                    {/* New Password */}
                    <motion.div variants={itemVariants} className="space-y-2">
                      <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        <KeyRound className="w-4 h-4" />
                        <span>Nouveau mot de passe</span>
                      </label>
                      <div className="relative group">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          placeholder="Créez un mot de passe sécurisé"
                          className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-12"
                          {...register("newPassword")}
                        />
                        <motion.button
                          type="button"
                          whileTap={{ scale: 0.95 }}
                          onClick={toggleNewPasswordVisibility}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                          {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </motion.button>
                      </div>
                      <AnimatePresence>
                        {errors.newPassword?.message && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="text-red-500 text-sm flex items-center space-x-1"
                          >
                            <X className="w-4 h-4" />
                            <span>{errors.newPassword.message}</span>
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </motion.div>

                    {/* Confirm Password */}
                    <motion.div variants={itemVariants} className="space-y-2">
                      <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        <Shield className="w-4 h-4" />
                        <span>Confirmer le mot de passe</span>
                      </label>
                      <div className="relative group">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirmez votre nouveau mot de passe"
                          className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-12"
                          {...register("passwordConfirm")}
                        />
                        <motion.button
                          type="button"
                          whileTap={{ scale: 0.95 }}
                          onClick={toggleConfirmPasswordVisibility}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </motion.button>
                      </div>
                      <AnimatePresence>
                        {errors.passwordConfirm?.message && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="text-red-500 text-sm flex items-center space-x-1"
                          >
                            <X className="w-4 h-4" />
                            <span>{errors.passwordConfirm.message}</span>
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </div>

                  {/* Right Column - Password Requirements */}
                  <motion.div variants={itemVariants} className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center space-x-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <span>Exigences de sécurité</span>
                    </h3>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 space-y-4">
                      {[
                        {
                          key: "minLength",
                          label: "8 caractères minimum",
                          requirement: passwordRequirements.minLength,
                        },
                        {
                          key: "hasLowercase",
                          label: "1 lettre minuscule",
                          requirement: passwordRequirements.hasLowercase,
                        },
                        {
                          key: "hasUppercase",
                          label: "1 lettre majuscule",
                          requirement: passwordRequirements.hasUppercase,
                        },
                        { key: "hasNumber", label: "1 chiffre", requirement: passwordRequirements.hasNumber },
                        {
                          key: "hasSpecialChar",
                          label: "1 caractère spécial",
                          requirement: passwordRequirements.hasSpecialChar,
                        },
                      ].map((item, index) => (
                        <motion.div
                          key={item.key}
                          variants={requirementVariants}
                          initial="hidden"
                          animate="visible"
                          transition={{ delay: index * 0.1 }}
                          className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 ${
                            item.requirement
                              ? "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800"
                              : "bg-gray-100 dark:bg-gray-600"
                          }`}
                        >
                          <div
                            className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                              item.requirement ? "bg-green-500" : "bg-gray-400"
                            }`}
                          >
                            {item.requirement ? (
                              <Check className="w-4 h-4 text-white" />
                            ) : (
                              <X className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <span
                            className={`text-sm font-medium ${
                              item.requirement
                                ? "text-green-700 dark:text-green-300"
                                : "text-gray-600 dark:text-gray-400"
                            }`}
                          >
                            {item.label}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                </div>

                {/* Submit Button */}
                <motion.div variants={itemVariants} className="flex justify-center pt-6">
                  <motion.button
                    type="submit"
                    disabled={isLoading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`
                      px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl
                      shadow-lg hover:shadow-xl transition-all duration-300 flex items-center space-x-2
                      ${isLoading ? "opacity-50 cursor-not-allowed" : "hover:from-blue-600 hover:to-purple-700"}
                    `}
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Mise à jour...</span>
                      </>
                    ) : (
                      <>
                        <Shield className="w-5 h-5" />
                        <span>Mettre à jour le mot de passe</span>
                      </>
                    )}
                  </motion.button>
                </motion.div>
              </form>
            </div>
          </motion.div>

          {/* Security Tips */}
          <motion.div
            variants={itemVariants}
            className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6"
          >
            <div className="flex items-start space-x-3">
              <Shield className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Conseils de sécurité</h4>
                <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                  <li>• Utilisez un mot de passe unique pour chaque compte</li>
                  <li>• Évitez d'utiliser des informations personnelles</li>
                  <li>• Changez votre mot de passe régulièrement</li>
                  <li>• Activez l'authentification à deux facteurs si disponible</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </CommonLayout>
  );
};

export default ClientAccountPass;
